<script setup>
// NOTICE: KAPTION-INTEGRATION
// rumo-carousels.DASHBOARD.1 — agents see the assistant's carousel read-only, inside
// the bubble: small horizontal cards (compact, tall) or rows (list), with each
// action's text and the Widget Studio destination it opens. Nothing is clickable;
// card actions never post to the chat.
// The bubble never grows past the message column: it takes a fixed width
// (capped at the column) and the card row scrolls inside it, so a wide set
// does not push an outgoing bubble out to the left.
import { computed, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseBubble from './Base.vue';
import { useMessageContext } from '../provider.js';
import {
  carouselCards,
  carouselLayoutOf,
} from 'shared/helpers/KaptionCarousels';

const { content, contentAttributes, contentType } = useMessageContext();
const { t } = useI18n();

const layout = computed(
  () =>
    carouselLayoutOf(contentType.value, contentAttributes.value) || 'compact'
);
const cards = computed(() => carouselCards(contentAttributes.value));
const layoutName = computed(
  () =>
    ({
      compact: t('CONVERSATION.KAPTION_CAROUSEL.LAYOUT.compact'),
      tall: t('CONVERSATION.KAPTION_CAROUSEL.LAYOUT.tall'),
      list: t('CONVERSATION.KAPTION_CAROUSEL.LAYOUT.list'),
    })[layout.value]
);
const failedImages = reactive(new Set());
// Two cards fit at their own width; more get a bubble wide enough to show a
// few, capped by the column (the row scrolls).
const widthClass = computed(() =>
  layout.value !== 'list' && cards.value.length > 2 ? 'w-[28rem]' : ''
);
</script>

<template>
  <BaseBubble
    class="min-w-0 max-w-full px-4 py-3"
    :class="widthClass"
    data-bubble-name="kaption-cards"
  >
    <span v-if="content" v-dompurify-html="content" :title="content" />
    <div class="mt-2 flex items-center gap-1.5 text-xs text-n-slate-11">
      <svg
        aria-hidden="true"
        width="13"
        height="13"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linejoin="round"
      >
        <path d="M3 6h12v12H3zM18 7v10M21 8v8" />
      </svg>
      <span>
        {{
          t('CONVERSATION.KAPTION_CAROUSEL.SUMMARY', {
            count: cards.length,
            layout: layoutName,
          })
        }}
      </span>
    </div>

    <ul
      v-if="layout === 'list'"
      class="mt-2 flex flex-col overflow-hidden rounded-lg border border-n-weak"
      data-kaption="carousel-preview"
      :data-layout="layout"
    >
      <li
        v-for="card in cards"
        :key="card.key"
        class="flex items-start gap-2.5 border-b border-n-weak px-2.5 py-2 last:border-b-0"
      >
        <img
          v-if="card.mediaUrl && !failedImages.has(card.key)"
          :src="card.mediaUrl"
          alt=""
          class="size-10 shrink-0 rounded-md bg-n-alpha-2 object-cover"
          @error="failedImages.add(card.key)"
        />
        <span v-else class="size-10 shrink-0 rounded-md bg-n-alpha-2" />
        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-1.5">
            <span class="truncate text-sm font-medium text-n-slate-12">
              {{ card.title }}
            </span>
            <span
              v-if="card.badge"
              class="shrink-0 rounded-full bg-n-alpha-2 px-1.5 text-xs font-medium text-n-slate-12"
            >
              {{ card.badge }}
            </span>
          </div>
          <p v-if="card.description" class="truncate text-xs text-n-slate-11">
            {{ card.description }}
          </p>
          <div v-if="card.actions.length" class="mt-1 flex flex-wrap gap-1">
            <span
              v-for="action in card.actions"
              :key="`${action.text}-${action.destination}`"
              class="rounded border border-n-weak px-1.5 text-xs text-n-slate-11"
              :title="t('CONVERSATION.KAPTION_CAROUSEL.OPENS')"
            >
              {{ action.text }}
              <span class="font-mono text-n-slate-10">
                {{ `→ ${action.destination}` }}
              </span>
            </span>
          </div>
        </div>
      </li>
    </ul>

    <div
      v-else
      class="mt-2 flex w-full min-w-0 max-w-full gap-2 overflow-x-auto pb-1"
      data-kaption="carousel-preview"
      :data-layout="layout"
    >
      <div
        v-for="card in cards"
        :key="card.key"
        class="w-40 shrink-0 overflow-hidden rounded-lg border border-n-weak bg-n-solid-1"
      >
        <div
          class="relative w-full bg-n-alpha-2"
          :class="layout === 'tall' ? 'h-24' : 'h-16'"
        >
          <img
            v-if="card.mediaUrl && !failedImages.has(card.key)"
            :src="card.mediaUrl"
            alt=""
            class="size-full object-cover"
            @error="failedImages.add(card.key)"
          />
          <span
            v-if="card.badge"
            class="absolute left-1.5 top-1.5 rounded-full bg-n-slate-12 px-1.5 text-xs font-semibold text-n-slate-1"
          >
            {{ card.badge }}
          </span>
        </div>
        <div class="flex flex-col gap-0.5 px-2 py-1.5">
          <span class="truncate text-sm font-medium text-n-slate-12">
            {{ card.title }}
          </span>
          <span v-if="card.description" class="text-xs text-n-slate-11">
            {{ card.description }}
          </span>
          <div v-if="card.actions.length" class="mt-1 flex flex-col gap-1">
            <span
              v-for="action in card.actions"
              :key="`${action.text}-${action.destination}`"
              class="flex flex-col rounded border border-n-weak px-1.5 py-0.5 text-xs text-n-slate-11"
              :title="t('CONVERSATION.KAPTION_CAROUSEL.OPENS')"
            >
              <span class="font-medium text-n-slate-12">{{ action.text }}</span>
              <span class="truncate font-mono text-n-slate-10">
                {{ `→ ${action.destination}` }}
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  </BaseBubble>
</template>
